import type { Ref } from "vue"
import { defineStore } from "pinia"
import { ref, watch } from "vue"
import useWebSocket from "@/hooks/useWebsocket"
import { useUserStoreHook } from "@/store/modules/user"
import { getToken } from "@/utils/cache/cookies"

export const useWsStore = defineStore("ws", () => {
  const data: Ref<string | null> = ref(null)
  const error: Ref<Event | null> = ref(null)
  const isOpen: Ref<boolean> = ref(false)
  const socket: Ref<WebSocket | null> = ref(null)
  const connectionEpoch = ref(0)
  const messageEpoch = ref(0)

  // 新增：保存 ws 实例和 shopId
  let wsInstance: ReturnType<typeof useWebSocket> | null = null
  let stopWsSync: (() => void) | undefined
  const lastShopId = ref<number | null>(null)
  let lastToken = ""

  function hasActiveSocket(target: WebSocket | null) {
    return target?.readyState === WebSocket.OPEN || target?.readyState === WebSocket.CONNECTING
  }

  const connect = (shopId: number) => {
    const currentToken = getToken() ?? ""
    if (lastShopId.value === shopId && lastToken === currentToken && hasActiveSocket(socket.value)) {
      return
    }
    stopWsSync?.()
    stopWsSync = undefined
    wsInstance?.disconnect()
    lastShopId.value = shopId
    lastToken = currentToken
    const url = `${import.meta.env.VITE_BASE_WS}/teashop_merchant/ws/business/${shopId}/0`

    //  const url = `ws://192.168.1.18:81/ws/business/11148/0`

    const userStore = useUserStoreHook()

    wsInstance = useWebSocket(
      url,
      (event: any) => {
        // onMessage callback
        data.value = event.data
        messageEpoch.value += 1
      },
      (event: any) => {
        // onError callback
        error.value = event
        console.error(event)
      },
      () => {
        // onClose callback
        isOpen.value = false
      },
      userStore.logout,
    )

    wsInstance.connect()
    let wasOpen = false
    stopWsSync = watch(
      [wsInstance.socket, wsInstance.isOpen],
      ([nextSocket, nextIsOpen]) => {
        socket.value = nextSocket
        isOpen.value = nextIsOpen
        if (nextIsOpen && !wasOpen) connectionEpoch.value += 1
        wasOpen = nextIsOpen
      },
      { immediate: true },
    )
  }

  const disconnect = () => {
    stopWsSync?.()
    stopWsSync = undefined
    wsInstance?.disconnect()
    wsInstance = null
    lastShopId.value = null
    lastToken = ""
    socket.value = null
    isOpen.value = false
  }

  function resetWebSocket() {
    if (wsInstance && typeof wsInstance.resetWebSocket === 'function') {
      wsInstance.resetWebSocket()
    }
  }

  const send = (data: string) => {
    if (socket.value && isOpen.value) {
      socket.value?.send(data)
    }
  }

  const getData = () => {
    return data.value
  }

  return {
    connect,
    disconnect,
    send,
    data,
    error,
    getData,
    isOpen,
    socket,
    connectionEpoch,
    messageEpoch,
    resetWebSocket, // 暴露
  }
})
